// IRC380 DMR / analog codeplug profiles (network-scoped) and per-radio codeplug copies
// (documentation/protocol.md "DMR codeplug profiles"). The server validates every codeplug; the
// types mirror its canonical JSON (server/pkg/dmrcodeplug).

export type CallType = 'PRIVATE' | 'GROUP' | 'ALL';
export type ChannelMode = 'DIGITAL' | 'ANALOG';
export type ToneType = 'NONE' | 'CTCSS' | 'DCS' | 'DCS_INVERTED';

export interface CodeplugContact {
  number: number;
  name: string;
  call_type: CallType;
  call_id: number;
}

export interface CodeplugRxGroupList {
  number: number;
  name: string;
  contacts: number[];
}

export interface CodeplugChannel {
  number: number;
  name: string;
  mode: ChannelMode;
  rx_frequency_hz: number;
  tx_frequency_hz: number;
  tx_power: 'LOW' | 'HIGH';
  rx_only: boolean;
  tx_admit: 'ALWAYS' | 'CHANNEL_FREE';
  // DIGITAL only
  color_code?: number;
  time_slot?: number;
  tx_contact?: number;
  rx_group_list?: number;
  dcdm?: boolean;
  // ANALOG only
  bandwidth?: 'NARROW' | 'WIDE';
  squelch?: number;
  rx_tone_type?: ToneType;
  rx_tone?: string;
  tx_tone_type?: ToneType;
  tx_tone?: string;
  tail_elimination?: boolean;
  tail_phase?: number;
}

export interface CodeplugZone {
  number: number;
  name: string;
  channels: number[];
}

export interface Codeplug {
  schema_version: 1;
  contacts: CodeplugContact[];
  rx_group_lists: CodeplugRxGroupList[];
  channels: CodeplugChannel[];
  zones: CodeplugZone[];
}

export interface CodeplugProfileSummary {
  id: string;
  org_id: string;
  name: string;
  description: string;
  revision: number;
  zone_count: number;
  channel_count: number;
  radio_count: number;
  created_at: string;
  updated_at: string;
}

export interface CodeplugProfile extends CodeplugProfileSummary {
  codeplug: Codeplug;
}

export interface CodeplugApplyReport {
  revision: number;
  result: 'APPLIED' | 'FAILED';
  error_code?: number;
  error?: string;
  reported_at: string;
}

export interface RadioCodeplugState {
  radio_id: string;
  dmr_supported: boolean;
  revision: number;
  assigned: boolean;
  source_profile_id: string | null;
  source_profile_name: string | null;
  source_profile_revision: number | null;
  source_profile_current_revision: number | null;
  locally_modified: boolean;
  loaded_at: string | null;
  updated_at: string | null;
  codeplug: Codeplug | null;
  last_apply_report: CodeplugApplyReport | null;
}

/** An API error with the server's code (e.g. REVISION_CONFLICT) and, for conflicts, the stored revision. */
export class CodeplugApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number, readonly currentRevision?: number) {
    super(message);
  }
}

const fail = async (response: Response, fallback: string): Promise<never> => {
  let body: { error?: string; code?: string; current_revision?: number } = {};
  try {
    body = await response.json();
  } catch {
    // not JSON (e.g. a proxy error page)
  }
  throw new CodeplugApiError(body.error || fallback, body.code || `HTTP_${response.status}`, response.status, body.current_revision);
};

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const profilesPath = (orgId: string) => `/api/admin/organizations/${encodeURIComponent(orgId)}/dmr-codeplug-profiles`;

export const listCodeplugProfiles = async (orgId: string): Promise<CodeplugProfileSummary[]> => {
  const response = await fetch(profilesPath(orgId));
  if (!response.ok) return fail(response, 'Failed to load codeplug profiles');
  return (await response.json()) ?? [];
};

export const getCodeplugProfile = async (orgId: string, id: string): Promise<CodeplugProfile> => {
  const response = await fetch(`${profilesPath(orgId)}/${encodeURIComponent(id)}`);
  if (!response.ok) return fail(response, 'Failed to load codeplug profile');
  return response.json();
};

export const createCodeplugProfile = async (orgId: string, name: string, description: string, codeplug: Codeplug): Promise<CodeplugProfile> => {
  const response = await fetch(profilesPath(orgId), json('POST', { name, description, codeplug }));
  if (!response.ok) return fail(response, 'Failed to create codeplug profile');
  return response.json();
};

export const updateCodeplugProfile = async (
  orgId: string,
  id: string,
  expectedRevision: number,
  name: string,
  description: string,
  codeplug: Codeplug
): Promise<CodeplugProfile> => {
  const response = await fetch(`${profilesPath(orgId)}/${encodeURIComponent(id)}`, json('PUT', { name, description, codeplug, expected_revision: expectedRevision }));
  if (!response.ok) return fail(response, 'Failed to save codeplug profile');
  return response.json();
};

/** Deletes a profile only if it is still at expectedRevision (409 REVISION_CONFLICT otherwise). */
export const deleteCodeplugProfile = async (orgId: string, id: string, expectedRevision: number): Promise<void> => {
  const response = await fetch(`${profilesPath(orgId)}/${encodeURIComponent(id)}?expected_revision=${expectedRevision}`, { method: 'DELETE' });
  if (!response.ok) return fail(response, 'Failed to delete codeplug profile');
};

const radioPath = (radioId: string) => `/api/admin/radios/${encodeURIComponent(radioId)}/dmr-codeplug`;

export const getRadioCodeplug = async (radioId: string): Promise<RadioCodeplugState> => {
  const response = await fetch(radioPath(radioId));
  if (!response.ok) return fail(response, 'Failed to load the radio codeplug');
  return response.json();
};

/** Replaces the radio's codeplug, including all radio-local edits, with a snapshot of the profile. */
export const loadProfileIntoRadio = async (radioId: string, profileId: string, expectedRevision: number): Promise<number> => {
  const response = await fetch(`${radioPath(radioId)}/load`, json('POST', { profile_id: profileId, expected_revision: expectedRevision }));
  if (!response.ok) return fail(response, 'Failed to load the profile into the radio');
  return (await response.json()).revision;
};

/** Saves this radio's own copy only (no profile and no other radio changes). */
export const updateRadioCodeplug = async (radioId: string, expectedRevision: number, codeplug: Codeplug): Promise<number> => {
  const response = await fetch(radioPath(radioId), json('PUT', { codeplug, expected_revision: expectedRevision }));
  if (!response.ok) return fail(response, 'Failed to save the radio codeplug');
  return (await response.json()).revision;
};

export const clearRadioCodeplug = async (radioId: string, expectedRevision: number): Promise<number> => {
  const response = await fetch(`${radioPath(radioId)}?expected_revision=${expectedRevision}`, { method: 'DELETE' });
  if (!response.ok) return fail(response, 'Failed to clear the radio codeplug');
  return (await response.json()).revision;
};

// Reading the codeplug the radio's DMR worker has currently loaded, to save it as a NEW profile
// (platform administrators only; the server enforces it). Nothing here writes to any radio: the
// capture is staged server-side and never returned to the browser.

export type CodeplugCaptureStatus = 'PENDING' | 'READY' | 'FAILED' | 'SAVED';

/** A reading problem; count null means it could not be determined (unknown), never "none". */
export interface CodeplugCaptureWarning {
  code: string;
  count: number | null;
  message: string;
}

export interface CodeplugCaptureSummary {
  contacts: number;
  rx_group_lists: number;
  channels: number;
  zones: number;
}

export interface CodeplugCapture {
  capture_id: string;
  radio_id: string;
  org_id: string;
  network_name: string;
  status: CodeplugCaptureStatus;
  created_at: string;
  expires_at: string;
  error_code: string | null;
  error: string | null;
  summary: CodeplugCaptureSummary | null;
  warnings: CodeplugCaptureWarning[];
  excluded: string[];
  default_name: string;
  profile: { id: string; name: string } | null;
}

export interface CodeplugCaptureSaved {
  capture: CodeplugCapture;
  profile: { id: string; name: string };
}

const capturesPath = (radioId: string) => `/api/admin/radios/${encodeURIComponent(radioId)}/dmr-codeplug-captures`;
const capturePath = (radioId: string, captureId: string) => `${capturesPath(radioId)}/${encodeURIComponent(captureId)}`;

/** Asks the radio to read its loaded codeplug (fails at once unless online, DMR READY and idle). */
export const startCodeplugCapture = async (radioId: string): Promise<CodeplugCapture> => {
  const response = await fetch(capturesPath(radioId), json('POST', { confirm_device_content_belongs_to_network: true }));
  if (!response.ok) return fail(response, 'Failed to start reading the codeplug from the radio');
  return response.json();
};

export const getCodeplugCapture = async (radioId: string, captureId: string): Promise<CodeplugCapture> => {
  const response = await fetch(capturePath(radioId, captureId));
  if (!response.ok) return fail(response, 'Failed to load the codeplug reading');
  return response.json();
};

/** Creates a NEW profile in the capture's network (idempotent after success). Loads it into no radio. */
export const saveCodeplugCapture = async (radioId: string, captureId: string, name: string, description: string): Promise<CodeplugCaptureSaved> => {
  const response = await fetch(
    `${capturePath(radioId, captureId)}/save`,
    json('POST', { name, description, confirm_device_content_belongs_to_network: true })
  );
  if (!response.ok) return fail(response, 'Failed to save the codeplug reading as a profile');
  return response.json();
};

export const discardCodeplugCapture = async (radioId: string, captureId: string): Promise<void> => {
  const response = await fetch(capturePath(radioId, captureId), { method: 'DELETE' });
  if (!response.ok) return fail(response, 'Failed to discard the codeplug reading');
};
